import React from "react";

import {
  Box,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  Divider as MuiDivider,
  Link,
  Typography,
  LinkProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { CancelSharp as CloseIcon } from "@mui/icons-material";

import { ReactComponent as PartnerFusionSvg } from "assets/images/icons/partners_fusion.svg";
import { Para16Text, Para14Text } from "components/shared/Typography";
import { colors } from "theme";
import { IDesign } from "types/design";

interface IFusionPartnerDialogProps {
  open: boolean;
  onClose: () => void;
  row: IDesign;
}

export const FusionPartnerDialog: React.FC<IFusionPartnerDialogProps> = ({
  open,
  onClose,
  row,
}) => {
  return (
    <Dialog open={open} fullWidth onClose={onClose}>
      <DialogTitle>
        <Box display="flex" alignItems="flex-start" gap="6px">
          <PartnerFusionSvg width={20} height={20} />
          <Box width={1} component="span">
            <Typography variant="h6" component="span">
              Fusion Partner
            </Typography>
          </Box>
          <CloseDialogButton onClick={onClose}>
            <CloseIcon />
          </CloseDialogButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Para16Text>
          Fusion detection with AMP<sup>&trade;</sup> chemistry only requires
          one partner to be targeted.
        </Para16Text>
        <Para16Text>
          Known fusion partners are suggested when a gene query is not included
          in our predesigned, functionally-tested content.
        </Para16Text>
        {Object.keys(row.coveredPartnerTargets || {}).length > 0 && (
          <>
            <Divider />
            <Box display="flex" alignItems="center" gap="4px">
              <PartnerFusionSvg width={20} height={20} />
              <Typography variant="h5" component="p">
                Targets with Known Fusion Partners (
                {row.numberCoveredPartnerTargets})
              </Typography>
            </Box>
            {Object.keys(row.coveredPartnerTargets || {})
              .sort()
              .map((key: string) => (
                <Box mt={2} display="flex" flexDirection="column" gap="16px">
                  <Typography variant="h6" component="p">
                    Your Query:{" "}
                    {(row.coveredGenes || []).find(
                      (gene) => gene.toLowerCase() === key.toLowerCase()
                    ) || ""}
                  </Typography>
                  <Para14Text>
                    Known Partners:{" "}
                    {(row.coveredPartnerTargets[key] as string[]).join(", ")}
                  </Para14Text>
                </Box>
              ))}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DialogTitle = styled(MuiDialogTitle)({
  background: colors.catSkill,
  border: `1px solid ${colors.grey[500]}`,
  borderRadius: "4px 4px 0 0",
  padding: "12px 16px 16px",
  boxSizing: "border-box",
});

const DialogContent = styled(MuiDialogContent)({
  padding: "16px 24px 32px !important",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 16,
  maxHeight: "70vh",
});

const CloseDialogButton = styled(
  (props: LinkProps<React.ElementType, { component?: React.ElementType }>) => (
    <Link component="button" {...props} />
  )
)({
  display: "flex",
  "& svg": {
    width: 20,
    height: 20,
    color: colors.grey[300],
  },
});

const Divider = styled(MuiDivider)({
  width: "100%",
  margin: "16px 0",
});
